import { ReactNode, useEffect, useMemo, useState } from 'react';
import { IconPlus, IconTrash, IconX } from '../../components/icons';
import { Field, useAsync, useToast } from '../../components/ui';
import { StepIcon, TRIGGER_NODE } from '../../journeys/JourneyCanvas';
import {
  Condition,
  Delivery,
  Duration,
  Graph,
  PropFilter,
  sampleVariables,
  Step,
  STEP_HINT,
  STEP_LABEL,
  StepType,
  TRIGGER_LABEL,
  Trigger,
  ValidationIssue,
  VariableSource,
} from '../../journeys/model';
import { defaultStep, insertAfter, isOpError, newStepId, removeStep } from '../../journeys/editor/graphOps';
import { portsOf, setTarget } from '../../journeys/editor/ports';
import { api, AttributeDef, Segment, Template, TemplatePreview, TemplateVersion, Webhook } from '../../lib/api';
import { t } from '../../lib/i18n';
import { errorText } from '../Contacts';
import { fromJson, Group, GroupEditor, toJson } from '../Segments';
import { SendNotificationForm } from '../../channels/JourneyNotifyForm';
import { ChannelChip, StepPill } from '../../channels/components';
import { Channel, eur, SKIP_REASON } from '../../channels/model';

/**
 * The settings of one step (docs/journeys/SPEC.md §4.1): a structured form over the fields of §3.2. Changes go to the
 * editor's draft (one undo entry per field within 1 s); «Сохранить версию» on the page makes a new immutable version.
 * Steps are added here with «+» next to any exit or «+ после шага», on the canvas by dragging (docs/pilot/SPEC.md §12).
 */

type Patch = (fn: (g: Graph) => void) => void;

const STEP_TYPES: StepType[] = ['send_email', 'send_notification', 'wait', 'wait_for_event', 'branch', 'split', 'update_contact', 'webhook', 'digest', 'exit'];

// ---------- small inputs ----------

type Unit = 'minutes' | 'hours' | 'days';
const UNIT_LABEL: Record<Unit, string> = { minutes: 'мин', hours: 'ч', days: 'дн.' };

function DurationInput({ value, onChange, units = ['minutes', 'hours', 'days'], disabled, label }: { value: Duration | undefined; onChange: (d: Duration) => void; units?: Unit[]; disabled: boolean; label: string }) {
  const minutes = (value?.days ?? 0) * 1440 + (value?.hours ?? 0) * 60 + (value?.minutes ?? 0);
  const unit: Unit = units.includes('days') && minutes % 1440 === 0 && minutes > 0 ? 'days' : units.includes('hours') && minutes % 60 === 0 && minutes > 0 ? 'hours' : 'minutes';
  const amount = unit === 'days' ? minutes / 1440 : unit === 'hours' ? minutes / 60 : minutes;
  const set = (n: number, u: Unit) => onChange({ [u]: Math.max(0, Math.round(n)) });
  return (
    <div className="inline-fields">
      <input className="input" style={{ width: 90 }} type="number" min={0} value={amount} onChange={(e) => set(Number(e.target.value), unit)} disabled={disabled} aria-label={label} />
      <select className="select input" value={unit} onChange={(e) => set(amount, e.target.value as Unit)} disabled={disabled} aria-label={t('{label}: единица', { label })}>
        {units.map((u) => <option key={u} value={u}>{t(UNIT_LABEL[u])}</option>)}
      </select>
    </div>
  );
}

/** A JSON value edited as text: applied when it parses. */
function JsonInput({ value, onChange, rows = 4, disabled, label, placeholder }: { value: unknown; onChange: (v: unknown) => void; rows?: number; disabled: boolean; label: string; placeholder?: string }) {
  const [text, setText] = useState(value === undefined ? '' : JSON.stringify(value, null, 2));
  const [bad, setBad] = useState(false);
  useEffect(() => setText(value === undefined ? '' : JSON.stringify(value, null, 2)), [JSON.stringify(value)]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <textarea
        className="input input-mono"
        rows={rows}
        value={text}
        spellCheck={false}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value.trim()) {
            setBad(false);
            onChange(undefined);
            return;
          }
          try {
            onChange(JSON.parse(e.target.value));
            setBad(false);
          } catch {
            setBad(true);
          }
        }}
      />
      {bad && <span className="err">{t('JSON не разбирается — изменение не применено')}</span>}
    </>
  );
}

function NextSelect({ graph, self, value, onChange, onInsert, disabled, label }: { graph: Graph; self: string; value: string | null; onChange: (v: string | null) => void; onInsert: (type: StepType) => void; disabled: boolean; label: string }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="inline-fields">
        <select className="select input" style={{ flex: 1 }} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} disabled={disabled} aria-label={label}>
          <option value="">{t('конец сценария')}</option>
          {Object.keys(graph.steps).filter((id) => id !== self).map((id) => <option key={id} value={id}>{id} · {t(STEP_LABEL[graph.steps[id].type])}</option>)}
        </select>
        {!disabled && <button type="button" className="btn btn-sm" onClick={() => setAdding((a) => !a)} aria-expanded={adding} title={t('Вставить новый шаг сюда')}><IconPlus size={14} /> {t('шаг')}</button>}
      </div>
      {adding && (
        <div className="chips">
          {STEP_TYPES.map((st) => (
            <button key={st} type="button" className="chip" title={t(STEP_HINT[st])} onClick={() => { setAdding(false); onInsert(st); }}>
              <StepIcon type={st} size={13} /> {t(STEP_LABEL[st])}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ConditionInput({ value, onChange, keys, segments, disabled, label }: { value: Condition | undefined; onChange: (c: Condition | undefined) => void; keys: AttributeDef[]; segments: Segment[]; disabled: boolean; label: string }) {
  const types = useMemo(() => new Map(keys.map((k) => [k.key, k.type])), [keys]);
  const parsed = value ? fromJson(value) : { t: 'group' as const, kind: 'all' as const, items: [] };
  const [json, setJson] = useState(!parsed);
  if (json || !parsed) {
    return (
      <div className="stack" style={{ gap: 6 }}>
        <JsonInput value={value} onChange={(v) => onChange(v as Condition | undefined)} rows={5} disabled={disabled} label={label} placeholder='{ "event": "order.paid", "within": { "days": 7 } }' />
        <div className="small muted">
          {t('Условия сценария: атрибуты, согласие, события, сегменты, а также')} <code>{'{ "context": "properties.total", "op": "gt", "value": 0 }'}</code>, <code>waited_event</code> {t('и')} <code>{'{ "step_message": "remind_1", "status": "accepted" }'}</code>.
          {parsed && <> <button type="button" className="btn btn-sm btn-ghost" onClick={() => setJson(false)}>{t('Конструктор')}</button></>}
        </div>
      </div>
    );
  }
  return (
    <div className="stack" style={{ gap: 6 }}>
      <GroupEditor group={parsed} onChange={(g: Group) => onChange(g.items.length ? (toJson(g, types) as Condition) : undefined)} keys={keys} segments={segments} depth={0} disabled={disabled} />
      <button type="button" className="btn btn-sm btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setJson(true)}>{t('JSON (условия сценария)')}</button>
    </div>
  );
}

function Issues({ issues, prefix }: { issues: ValidationIssue[]; prefix: string }) {
  const mine = prefix ? issues.filter((i) => i.path === prefix || i.path.startsWith(`${prefix}.`) || i.path.startsWith(`${prefix}[`)) : issues;
  if (!mine.length) return null;
  return (
    <ul className="issues">
      {mine.map((i, n) => (
        <li key={n} className={i.code && !i.hint ? 'warning' : ''}>
          <code>{i.path.slice(prefix.length).replace(/^\./, '') || i.path}</code> — {i.message}
          {i.hint && <> · {i.hint}</>}
          {i.did_you_mean && <> · {t('может быть,')} <b>{i.did_you_mean}</b>?</>}
        </li>
      ))}
    </ul>
  );
}

// ---------- the panel ----------

export interface PanelData {
  templates: Template[];
  webhooks: Webhook[];
  keys: AttributeDef[];
  segments: Segment[];
}

export function usePanelData() {
  const templates = useAsync(() => api.get<Template[]>('/templates'));
  const webhooks = useAsync(() => api.get<Webhook[]>('/webhooks').catch(() => [] as Webhook[]));
  const schema = useAsync(() => api.get<{ items: AttributeDef[] }>('/contacts/attributes'));
  const segments = useAsync(() => api.get<Segment[]>('/segments'));
  return { templates: templates.data ?? [], webhooks: webhooks.data ?? [], keys: schema.data?.items ?? [], segments: segments.data ?? [] } satisfies PanelData;
}

export function JourneyStepPanel({
  graph,
  id,
  patch,
  onSelect,
  onClose,
  data,
  issues,
  disabled,
  testVersion,
  journeyId,
}: {
  graph: Graph;
  id: string;
  patch: Patch;
  onSelect: (id: string | null) => void;
  onClose: () => void;
  data: PanelData;
  issues: ValidationIssue[];
  disabled: boolean;
  /** Saved version the step can be tested on (null while the draft has unsaved changes). */
  testVersion: number | null;
  journeyId: string;
}) {
  if (id === TRIGGER_NODE) return <JourneySettings graph={graph} patch={patch} onClose={onClose} data={data} issues={issues} disabled={disabled} />;
  const step = graph.steps[id];
  if (!step) return null;
  const setStep = (fn: (s: Step) => void) => patch((g) => fn(g.steps[id]));

  /** Inserts a new step at an exit: the new step continues to where the exit pointed (graphOps defaults). */
  const insertAt = (get: (s: Step) => string | null, set: (s: Step, v: string) => void) => (type: StepType) => {
    let created = '';
    patch((g) => {
      const s = g.steps[id];
      const old = get(s);
      created = newStepId(g, type);
      const fresh = defaultStep(type);
      g.steps[created] = fresh;
      for (const key of portsOf(fresh)) setTarget(g, { step: created, key }, old);
      set(s, created);
    });
    setTimeout(() => onSelect(created), 0);
  };

  /** «+ после шага» (docs/pilot/SPEC.md §12.4): into the first free exit, else between the step and its target. */
  const insertAfterThis = (type: StepType) => {
    let created = '';
    patch((g) => {
      const r = insertAfter(g, id, type);
      if (isOpError(r)) return;
      created = r.id;
      g.steps = r.graph.steps;
      g.start = r.graph.start;
    });
    setTimeout(() => created && onSelect(created), 0);
  };

  function rename(next: string) {
    if (!/^[a-z0-9_]{1,40}$/.test(next) || graph.steps[next]) return;
    patch((g) => {
      const repoint = (v: string | null) => (v === id ? next : v);
      g.steps[next] = g.steps[id];
      delete g.steps[id];
      if (g.start === id) g.start = next;
      for (const s of Object.values(g.steps)) {
        if ('next' in s) s.next = repoint(s.next);
        if (s.type === 'wait_for_event') {
          s.on_event = repoint(s.on_event);
          s.on_timeout = repoint(s.on_timeout);
        }
        if (s.type === 'branch') {
          s.conditions.forEach((c) => (c.next = repoint(c.next)));
          s.else = repoint(s.else);
        }
        if (s.type === 'split') s.variants.forEach((v) => (v.next = repoint(v.next)));
      }
      if (g.layout?.[id]) {
        g.layout[next] = g.layout[id];
        delete g.layout[id];
      }
    });
    setTimeout(() => onSelect(next), 0);
  }

  function remove() {
    if (!confirm(t('Удалить шаг «{id}»? Ссылки на него перейдут к следующему шагу (если выход один) или станут «конец ветки».', { id }))) return;
    patch((g) => {
      const r = removeStep(g, id, 'heal');
      g.steps = r.steps;
      g.start = r.start;
    });
    onClose();
  }

  const next = (label: string, get: (s: Step) => string | null, set: (s: Step, v: string | null) => void) => (
    <Field label={label}>
      <NextSelect graph={graph} self={id} value={get(step)} onChange={(v) => setStep((s) => set(s, v))} onInsert={insertAt(get, (s, v) => set(s, v))} disabled={disabled} label={label} />
    </Field>
  );

  let body: ReactNode = null;
  switch (step.type) {
    case 'wait':
      body = (
        <>
          <div className="chips">
            <button type="button" className={`chip${!step.until_local ? ' on' : ''}`} disabled={disabled} onClick={() => setStep((s) => { const w = s as Extract<Step, { type: 'wait' }>; delete w.until_local; w.duration = w.duration ?? { days: 1 }; })}>{t('Длительность')}</button>
            <button type="button" className={`chip${step.until_local ? ' on' : ''}`} disabled={disabled} onClick={() => setStep((s) => { const w = s as Extract<Step, { type: 'wait' }>; delete w.duration; w.until_local = w.until_local ?? { time: '10:00' }; })}>{t('До местного времени')}</button>
          </div>
          {step.until_local ? (
            <>
              <Field label={t('Время контакта')} hint={t('Ближайшее такое время в поясе контакта (переходы на летнее время учтены)')}>
                <input className="input" type="time" value={step.until_local.time} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'wait' }>).until_local!.time = e.target.value))} disabled={disabled} />
              </Field>
              <Field label={t('Дни недели')} hint={t('Не выбрано — любой день')}>
                <div className="chips">
                  {['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'].map((d, i) => {
                    const on = step.until_local?.weekdays?.includes(i + 1);
                    return (
                      <button key={d} type="button" className={`chip${on ? ' on' : ''}`} disabled={disabled} onClick={() => setStep((s) => {
                        const u = (s as Extract<Step, { type: 'wait' }>).until_local!;
                        const set = new Set(u.weekdays ?? []);
                        if (set.has(i + 1)) set.delete(i + 1); else set.add(i + 1);
                        if (set.size) u.weekdays = [...set].sort(); else delete u.weekdays;
                      })}>{t(d)}</button>
                    );
                  })}
                </div>
              </Field>
            </>
          ) : (
            <Field label={t('Подождать')}><DurationInput value={step.duration} onChange={(d) => setStep((s) => ((s as Extract<Step, { type: 'wait' }>).duration = d))} disabled={disabled} label={t('Подождать')} /></Field>
          )}
          {next(t('Дальше'), (s) => (s as { next: string | null }).next, (s, v) => ((s as { next: string | null }).next = v))}
        </>
      );
      break;
    case 'wait_for_event':
      body = (
        <>
          <Field label={t('Событие')}><input className="input input-mono" value={step.event} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'wait_for_event' }>).event = e.target.value.trim()))} disabled={disabled} /></Field>
          <Field label={t('Фильтр свойств (JSON)')} hint={t('Например [{ "property": "cart_id", "op": "eq", "value": { "ref": "context.properties.cart_id" } }]')}>
            <JsonInput value={step.where} onChange={(v) => setStep((s) => { const w = s as Extract<Step, { type: 'wait_for_event' }>; if (v === undefined) delete w.where; else w.where = v as PropFilter[]; })} disabled={disabled} label={t('Фильтр свойств')} rows={3} />
          </Field>
          <Field label={t('Ждать не дольше')} hint={t('До 90 дней')}><DurationInput value={step.timeout} onChange={(d) => setStep((s) => ((s as Extract<Step, { type: 'wait_for_event' }>).timeout = d))} disabled={disabled} label={t('Тайм-аут')} /></Field>
          {next(t('Если событие пришло'), (s) => (s as Extract<Step, { type: 'wait_for_event' }>).on_event, (s, v) => ((s as Extract<Step, { type: 'wait_for_event' }>).on_event = v))}
          {next(t('Если тайм-аут'), (s) => (s as Extract<Step, { type: 'wait_for_event' }>).on_timeout, (s, v) => ((s as Extract<Step, { type: 'wait_for_event' }>).on_timeout = v))}
        </>
      );
      break;
    case 'branch':
      body = (
        <>
          <div className="small muted">{t('Условия проверяются по порядку; первое истинное решает.')}</div>
          {step.conditions.map((c, i) => (
            <div key={i} className="card card-pad stack" style={{ gap: 8 }}>
              <div className="row">
                <b className="small">{t('Условие {n}', { n: i + 1 })}</b>
                <span className="spacer" />
                {!disabled && step.conditions.length > 1 && <button type="button" className="btn btn-sm btn-ghost" aria-label={t('Удалить условие {n}', { n: i + 1 })} onClick={() => setStep((s) => (s as Extract<Step, { type: 'branch' }>).conditions.splice(i, 1))}><IconTrash size={14} /></button>}
              </div>
              <ConditionInput value={c.when} onChange={(v) => setStep((s) => ((s as Extract<Step, { type: 'branch' }>).conditions[i].when = v ?? { all: [] }))} keys={data.keys} segments={data.segments} disabled={disabled} label={t('Условие {n}', { n: i + 1 })} />
              {next(t('Тогда'), (s) => (s as Extract<Step, { type: 'branch' }>).conditions[i]?.next ?? null, (s, v) => ((s as Extract<Step, { type: 'branch' }>).conditions[i].next = v))}
            </div>
          ))}
          {!disabled && step.conditions.length < 10 && <button type="button" className="btn btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setStep((s) => (s as Extract<Step, { type: 'branch' }>).conditions.push({ when: { consent: 'marketing', status: 'granted' }, next: null }))}><IconPlus size={14} /> {t('Условие')}</button>}
          {next(t('Иначе'), (s) => (s as Extract<Step, { type: 'branch' }>).else, (s, v) => ((s as Extract<Step, { type: 'branch' }>).else = v))}
        </>
      );
      break;
    case 'split': {
      const sum = step.variants.reduce((a, v) => a + (Number(v.weight) || 0), 0);
      body = (
        <>
          <div className="small muted">{t('Вариант назначается детерминированно по контакту и сохраняется в участии.')}</div>
          {step.variants.map((v, i) => (
            <div key={i} className="card card-pad stack" style={{ gap: 8 }}>
              <div className="inline-fields">
                <input className="input input-mono" style={{ width: 80 }} value={v.id} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'split' }>).variants[i].id = e.target.value.trim()))} disabled={disabled} aria-label={t('Вариант')} />
                <input className="input" style={{ width: 80 }} type="number" min={0} max={100} value={v.weight} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'split' }>).variants[i].weight = Number(e.target.value)))} disabled={disabled} aria-label={t('Вес, %')} />
                <span className="small muted">%</span>
                <span className="spacer" />
                {!disabled && step.variants.length > 2 && <button type="button" className="btn btn-sm btn-ghost" aria-label={t('Удалить вариант {id}', { id: v.id })} onClick={() => setStep((s) => (s as Extract<Step, { type: 'split' }>).variants.splice(i, 1))}><IconTrash size={14} /></button>}
              </div>
              {next(t('Вариант {id}: дальше', { id: v.id }), (s) => (s as Extract<Step, { type: 'split' }>).variants[i]?.next ?? null, (s, val) => ((s as Extract<Step, { type: 'split' }>).variants[i].next = val))}
            </div>
          ))}
          <div className={`small ${sum === 100 ? 'muted' : ''}`} style={sum === 100 ? undefined : { color: '#d9433d' }}>{t('Сумма весов: {s} %', { s: sum })} {sum === 100 ? '' : t('— должна быть 100')}</div>
          {!disabled && step.variants.length < 5 && <button type="button" className="btn btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setStep((s) => (s as Extract<Step, { type: 'split' }>).variants.push({ id: String.fromCharCode(97 + step.variants.length), weight: 0, next: null }))}><IconPlus size={14} /> {t('Вариант')}</button>}
        </>
      );
      break;
    }
    case 'send_email':
      body = <SendEmailForm graph={graph} id={id} step={step} setStep={setStep} data={data} disabled={disabled} next={next(t('Дальше'), (s) => (s as { next: string | null }).next, (s, v) => ((s as { next: string | null }).next = v))} />;
      break;
    case 'send_notification':
      body = <SendNotificationForm graph={graph} step={step} setStep={setStep} disabled={disabled} attributeKeys={data.keys.map((k) => k.key)} next={next(t('Дальше'), (s) => (s as { next: string | null }).next, (s, v) => ((s as { next: string | null }).next = v))} />;
      break;
    case 'webhook': {
      const eligible = data.webhooks.filter((w) => w.events.includes('journey.webhook'));
      body = (
        <>
          <Field label={t('Эндпоинт')} hint={eligible.length ? t('Вебхук ставится в очередь в транзакции шага; сценарий не ждёт ответа') : t('Нет эндпоинтов с событием journey.webhook — добавьте его в разделе «Вебхуки»')}>
            <select className="select input" value={step.endpoint_id} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'webhook' }>).endpoint_id = e.target.value))} disabled={disabled}>
              <option value="">{t('— выберите —')}</option>
              {eligible.map((w) => <option key={w.id} value={w.id}>{w.url}{w.enabled ? '' : ` (${t('выключен')})`}</option>)}
              {step.endpoint_id && !eligible.some((w) => w.id === step.endpoint_id) && <option value={step.endpoint_id}>{step.endpoint_id}</option>}
            </select>
          </Field>
          <Field label={t('Атрибуты контакта в теле')} hint={t('Через запятую, до 20')}>
            <input className="input input-mono" value={(step.include_attributes ?? []).join(', ')} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'webhook' }>).include_attributes = e.target.value.split(',').map((x) => x.trim()).filter(Boolean)))} disabled={disabled} />
          </Field>
          {next(t('Дальше'), (s) => (s as { next: string | null }).next, (s, v) => ((s as { next: string | null }).next = v))}
        </>
      );
      break;
    }
    case 'update_contact':
      body = (
        <>
          <Field label={t('Записать атрибуты')} hint={t('Значение или путь (contact.attributes.x, context.properties.y)')}>
            <div className="stack" style={{ gap: 6 }}>
              {Object.entries(step.set ?? {}).map(([k, v]) => (
                <div key={k} className="kv-row">
                  <code style={{ overflowWrap: 'anywhere' }}>{k}</code>
                  <input
                    className="input input-mono"
                    value={v && typeof v === 'object' ? `=${v.path}` : JSON.stringify(v)}
                    onChange={(e) => setStep((s) => {
                      const raw = e.target.value;
                      let val: unknown = raw;
                      if (raw.startsWith('=')) val = { path: raw.slice(1).trim() };
                      else {
                        try { val = JSON.parse(raw); } catch { val = raw; }
                      }
                      (s as Extract<Step, { type: 'update_contact' }>).set[k] = val as never;
                    })}
                    disabled={disabled}
                    aria-label={t('Значение {k}', { k })}
                    title={t('true, 42, «текст» или =путь')}
                  />
                  {!disabled && <button type="button" className="btn btn-sm btn-ghost" aria-label={t('Убрать {k}', { k })} onClick={() => setStep((s) => delete (s as Extract<Step, { type: 'update_contact' }>).set[k])}><IconTrash size={14} /></button>}
                </div>
              ))}
              {!disabled && <AddKey keys={data.keys.map((k) => k.key)} onAdd={(k) => setStep((s) => ((s as Extract<Step, { type: 'update_contact' }>).set[k] = true))} />}
            </div>
          </Field>
          <Field label={t('Удалить атрибуты')} hint={t('Через запятую')}>
            <input className="input input-mono" value={(step.unset ?? []).join(', ')} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'update_contact' }>).unset = e.target.value.split(',').map((x) => x.trim()).filter(Boolean)))} disabled={disabled} />
          </Field>
          {next(t('Дальше'), (s) => (s as { next: string | null }).next, (s, v) => ((s as { next: string | null }).next = v))}
        </>
      );
      break;
    case 'digest':
      body = (
        <>
          <Field label={t('Собирать событие')}><input className="input input-mono" value={step.event} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'digest' }>).event = e.target.value.trim()))} disabled={disabled} /></Field>
          <Field label={t('Окно')} hint={t('До 24 ч')}><DurationInput value={step.window} units={['minutes', 'hours']} onChange={(d) => setStep((s) => ((s as Extract<Step, { type: 'digest' }>).window = d as { minutes?: number; hours?: number }))} disabled={disabled} label={t('Окно')} /></Field>
          <Field label={t('Не больше элементов')}><input className="input" type="number" min={1} max={50} value={step.max_items} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'digest' }>).max_items = Number(e.target.value)))} disabled={disabled} /></Field>
          <Field label={t('Строка элемента')} hint={t('{{свойство}} события; в письме — {{digest.items_text}} / {{{digest.items_html}}}')}><input className="input input-mono" value={step.item_template} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'digest' }>).item_template = e.target.value))} disabled={disabled} /></Field>
          {next(t('Дальше'), (s) => (s as { next: string | null }).next, (s, v) => ((s as { next: string | null }).next = v))}
        </>
      );
      break;
    case 'exit':
      body = (
        <Field label={t('Причина выхода')} hint={t('латиница, цифры и _, видна в журнале участия')}>
          <input className="input input-mono" value={step.reason} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'exit' }>).reason = e.target.value.trim()))} disabled={disabled} />
        </Field>
      );
      break;
  }

  return (
    <div className="card step-panel">
      <div className="card-head">
        <h3 className="row" style={{ gap: 8 }}><StepIcon type={step.type} /> {t(STEP_LABEL[step.type])}</h3>
        <button className="btn btn-ghost icon-btn" onClick={onClose} aria-label={t('Закрыть панель')}><IconX /></button>
      </div>
      <div className="card-pad stack">
        <div className="small muted">{t(STEP_HINT[step.type])}</div>
        <Field label={t('Id шага')} hint={graph.start === id ? t('Первый шаг сценария') : t('Переименование обновит все ссылки')}>
          <RenameInput id={id} onRename={rename} disabled={disabled} taken={(v) => !!graph.steps[v] && v !== id} />
        </Field>
        <Issues issues={issues} prefix={`steps.${id}`} />
        {body}
        {!disabled && step.type !== 'exit' && <InsertAfter onInsert={insertAfterThis} />}
        {graph.start !== id && !disabled && (
          <div className="row wrap" style={{ gap: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
            <button type="button" className="btn btn-sm" onClick={() => patch((g) => { g.start = id; })}>{t('Сделать первым')}</button>
            <span className="spacer" />
            <button type="button" className="btn btn-sm btn-danger" onClick={remove}><IconTrash size={14} /> {t('Удалить шаг')}</button>
          </div>
        )}
        <StepTest journeyId={journeyId} version={testVersion} step={id} type={step.type} disabled={disabled} />
      </div>
    </div>
  );
}

function InsertAfter({ onInsert }: { onInsert: (type: StepType) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="stack" style={{ gap: 6 }}>
      <button type="button" className="btn btn-sm" style={{ alignSelf: 'flex-start' }} aria-expanded={open} onClick={() => setOpen((o) => !o)}><IconPlus size={14} /> {t('после шага')}</button>
      {open && (
        <div className="chips">
          {STEP_TYPES.map((st) => (
            <button key={st} type="button" className="chip" title={t(STEP_HINT[st])} onClick={() => { setOpen(false); onInsert(st); }}>
              <StepIcon type={st} size={13} /> {t(STEP_LABEL[st])}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RenameInput({ id, onRename, disabled, taken }: { id: string; onRename: (v: string) => void; disabled: boolean; taken: (v: string) => boolean }) {
  const [v, setV] = useState(id);
  useEffect(() => setV(id), [id]);
  const ok = /^[a-z0-9_]{1,40}$/.test(v) && !taken(v);
  return (
    <div className="inline-fields">
      <input className="input input-mono" style={{ flex: 1 }} value={v} onChange={(e) => setV(e.target.value.toLowerCase())} disabled={disabled} aria-label={t('Id шага')} />
      {v !== id && <button type="button" className="btn btn-sm" disabled={!ok} onClick={() => onRename(v)}>{t('Переименовать')}</button>}
    </div>
  );
}

function AddKey({ keys, onAdd }: { keys: string[]; onAdd: (k: string) => void }) {
  const [k, setK] = useState('');
  return (
    <div className="inline-fields">
      <input className="input input-mono" style={{ flex: 1 }} list="journey-attr-keys" placeholder={t('ключ атрибута')} value={k} onChange={(e) => setK(e.target.value.trim())} aria-label={t('Новый атрибут')} />
      <datalist id="journey-attr-keys">{keys.map((x) => <option key={x} value={x} />)}</datalist>
      <button type="button" className="btn btn-sm" disabled={!/^[a-z][a-z0-9_]{0,63}$/.test(k)} onClick={() => { onAdd(k); setK(''); }}><IconPlus size={14} /> {t('Атрибут')}</button>
    </div>
  );
}

// ---------- send_email ----------

/** A template version as GET /templates/:id/versions returns it (with its text). */
type VersionContent = TemplateVersion & { html: string | null; text: string | null };

const PATHS = ['contact.email', 'contact.external_id', 'context.properties.', 'waited_event.properties.', 'digest.count', 'digest.items_text', 'digest.items_html', 'unsubscribe_url'];

function SendEmailForm({ graph, id, step, setStep, data, disabled, next }: { graph: Graph; id: string; step: Extract<Step, { type: 'send_email' }>; setStep: (fn: (s: Step) => void) => void; data: PanelData; disabled: boolean; next: ReactNode }) {
  const toast = useToast();
  const tpl = data.templates.find((x) => x.alias === step.template.alias || x.id === step.template.id);
  const versions = useAsync(() => (tpl ? api.get<VersionContent[]>(`/templates/${tpl.id}/versions`) : Promise.resolve([] as VersionContent[])), [tpl?.id]);
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const pinned = typeof step.template.version === 'number' ? step.template.version : null;
  const content = versions.data?.find((v) => v.version === (pinned ?? tpl?.version));
  const vars = useMemo(() => {
    if (!content) return tpl?.variables ?? [];
    const names = new Set<string>();
    for (const src of [content.subject, content.html ?? '', content.text ?? '']) for (const m of src.matchAll(/\{\{\{?\s*([A-Za-z_][\w.]*)\s*\}?\}\}/g)) names.add(m[1]);
    return [...names].sort();
  }, [content, tpl]);
  const stream = step.stream ?? graph.delivery.stream;

  function pickTemplate(alias: string) {
    const picked = data.templates.find((x) => x.alias === alias);
    setStep((s) => {
      const e = s as Extract<Step, { type: 'send_email' }>;
      e.template = { alias, version: 'latest' };
      for (const v of picked?.variables ?? []) if (v !== 'unsubscribe_url' && !(v in e.variables)) e.variables[v] = { path: `contact.attributes.${v}` };
    });
    setPreview(null);
  }

  async function showPreview() {
    if (!content) return;
    try {
      setPreview(await api.post<TemplatePreview>('/templates/preview', { subject: content.subject, html: content.html, text: content.text, variables: sampleVariables(vars) }));
    } catch (err) {
      toast(errorText(err), 'bad');
    }
  }

  return (
    <>
      <Field label={t('Шаблон письма')} hint={data.templates.length ? undefined : t('Шаблонов нет — создайте в разделе «Шаблоны»')}>
        <select className="select input" value={step.template.alias ?? ''} onChange={(e) => pickTemplate(e.target.value)} disabled={disabled}>
          <option value="">{t('— выберите —')}</option>
          {data.templates.map((x) => <option key={x.id} value={x.alias}>{x.name} · {x.alias}</option>)}
          {step.template.alias && !tpl && <option value={step.template.alias}>{t('{a} (не найден)', { a: step.template.alias })}</option>}
        </select>
      </Field>
      {tpl && (
        <Field label={t('Версия текста')} hint={t('Закрепляется при сохранении: одобряется ровно этот текст, правки шаблона потом его не меняют')}>
          <select className="select input" value={pinned ?? 'latest'} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'send_email' }>).template = { alias: tpl.alias, version: e.target.value === 'latest' ? 'latest' : Number(e.target.value) }))} disabled={disabled}>
            <option value="latest">{t('последняя (сейчас v{v})', { v: tpl.version })}</option>
            {(versions.data ?? []).map((v) => <option key={v.version} value={v.version}>v{v.version} · {v.subject}</option>)}
          </select>
        </Field>
      )}
      {content && (
        <div className="stack" style={{ gap: 6 }}>
          <div className="row wrap"><span className="small">{t('Тема:')} <b>{content.subject}</b></span><span className="spacer" /><button type="button" className="btn btn-sm" onClick={showPreview}>{t('Предпросмотр')}</button></div>
          {preview && (
            <>
              {preview.html ? <iframe title={t('Предпросмотр письма {id}', { id })} sandbox="" srcDoc={preview.html} className="mail-preview" style={{ height: 300 }} /> : <pre className="code" style={{ whiteSpace: 'pre-wrap' }}>{preview.text}</pre>}
              <div className="small muted">{t('Переменные показаны как [имя]; ссылка отписки подставляется при отправке.')}</div>
            </>
          )}
        </div>
      )}
      {vars.length > 0 && (
        <Field label={t('Переменные шаблона')} hint={t('Путь — начинается с «=»: =contact.attributes.first_name; иначе — постоянное значение')}>
          <div className="stack" style={{ gap: 6 }}>
            {vars.map((name) => {
              if (name === 'unsubscribe_url') return <div key={name} className="small"><code>unsubscribe_url</code> — {t('подставляется автоматически')} {stream === 'marketing' ? t('(обязательна в маркетинге)') : ''}</div>;
              const src = step.variables[name] as VariableSource | undefined;
              const isPath = src && typeof src === 'object';
              const text = src === undefined ? '' : isPath ? `=${(src as { path: string }).path}` : String(src);
              return (
                <div key={name} className="kv-row">
                  <code style={{ overflowWrap: 'anywhere' }}>{name}</code>
                  <input
                    className="input input-mono"
                    list="journey-paths"
                    value={text}
                    placeholder="=contact.attributes.…"
                    onChange={(e) => setStep((s) => {
                      const v = (s as Extract<Step, { type: 'send_email' }>).variables;
                      const raw = e.target.value;
                      if (!raw) delete v[name];
                      else if (raw.startsWith('=')) v[name] = { path: raw.slice(1).trim(), ...(isPath && (src as { default?: unknown }).default !== undefined ? { default: (src as { default: string }).default } : {}) };
                      else v[name] = raw;
                    })}
                    disabled={disabled}
                    aria-label={t('Источник {name}', { name })}
                  />
                  {isPath ? (
                    <input className="input" style={{ width: 96 }} placeholder={t('по умолч.')} value={String((src as { default?: unknown }).default ?? '')} onChange={(e) => setStep((s) => {
                      const v = (s as Extract<Step, { type: 'send_email' }>).variables[name] as { path: string; default?: string };
                      if (e.target.value) v.default = e.target.value; else delete v.default;
                    })} disabled={disabled} aria-label={t('Значение по умолчанию {name}', { name })} />
                  ) : <span />}
                </div>
              );
            })}
            <datalist id="journey-paths">
              {[...data.keys.map((k) => `=contact.attributes.${k.key}`), ...PATHS.map((p) => `=${p}`)].map((p) => <option key={p} value={p} />)}
            </datalist>
          </div>
        </Field>
      )}
      <Field label={t('Поток')} hint={stream === 'transactional' ? t('Сервисное: без согласия, без тихих часов и лимитов; согласующий подтверждает, что рекламы нет') : t('Маркетинг: нужно согласие, отписка, тихие часы и умная отправка')}>
        <select className="select input" value={step.stream ?? ''} onChange={(e) => setStep((s) => { const x = s as Extract<Step, { type: 'send_email' }>; if (e.target.value) x.stream = e.target.value as Delivery['stream']; else delete x.stream; })} disabled={disabled}>
          <option value="">{t('как у сценария ({s})', { s: graph.delivery.stream === 'marketing' ? t('маркетинг') : t('транзакционные') })}</option>
          <option value="marketing">{t('маркетинг')}</option>
          <option value="transactional">{t('транзакционные (сервисные)')}</option>
        </select>
      </Field>
      {stream === 'marketing' && (
        <label className="row small" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={step.smart_sending !== false} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'send_email' }>).smart_sending = e.target.checked))} disabled={disabled} />
          {t('Умная отправка: не слать, если недавно было письмо сценария ({h} ч)', { h: graph.delivery.smart_sending_hours ?? 16 })}
        </label>
      )}
      <Field label={t('Если письмо пропущено')} hint={t('Нет согласия, подавление, лимит, опоздание')}>
        <select className="select input" value={step.on_skip ?? 'continue'} onChange={(e) => setStep((s) => ((s as Extract<Step, { type: 'send_email' }>).on_skip = e.target.value as 'continue' | 'exit'))} disabled={disabled}>
          <option value="continue">{t('идти дальше')}</option>
          <option value="exit">{t('завершить участие')}</option>
        </select>
      </Field>
      <Field label={t('Отправитель шага')} hint={t('По умолчанию — {e}', { e: graph.delivery.from?.email ?? t('отправитель сценария') })}>
        <input className="input" type="email" placeholder={graph.delivery.from?.email ?? t('hello@ваш-домен')} value={step.from?.email ?? ''} onChange={(e) => setStep((s) => { const x = s as Extract<Step, { type: 'send_email' }>; if (e.target.value.trim()) x.from = { ...(x.from ?? {}), email: e.target.value.trim() }; else delete x.from; })} disabled={disabled} />
      </Field>
      {next}
    </>
  );
}

// ---------- test of a step ----------

function StepTest({ journeyId, version, step, type, disabled }: { journeyId: string; version: number | null; step: string; type: StepType; disabled: boolean }) {
  const toast = useToast();
  const [contact, setContact] = useState('');
  const [ctx, setCtx] = useState('');
  const [res, setRes] = useState<{ decision: Record<string, unknown>; email?: { subject: string | null; html: string | null; text: string | null; missing_variables: string[] }; notification?: { steps: { seq: number; channel: Channel; state: string; skip_reason?: string; estimated_cost: string | null }[]; estimated_cost_max?: { amount: string }; error?: { code: string; message: string } } } | null>(null);
  if (disabled) return null;
  async function run() {
    try {
      let context: Record<string, unknown> | undefined;
      if (ctx.trim()) context = JSON.parse(ctx);
      setRes(await api.post(`/journeys/${journeyId}/versions/${version}/steps/${step}/test`, { contact: contact.trim(), ...(context ? { context } : {}) }));
    } catch (err) {
      toast(err instanceof SyntaxError ? t('Контекст: JSON не разбирается') : errorText(err), 'bad');
    }
  }
  return (
    <details style={{ paddingTop: 8, borderTop: '1px solid var(--border)' }}>
      <summary className="small" style={{ cursor: 'pointer', fontWeight: 700 }}>{t('Тест шага на контакте')}</summary>
      {version === null ? (
        <p className="small muted">{t('Сохраните версию — тест идёт по сохранённому графу.')}</p>
      ) : (
        <div className="stack" style={{ gap: 8, marginTop: 8 }}>
          <input className="input" placeholder={t('email, external_id или con_…')} value={contact} onChange={(e) => setContact(e.target.value)} aria-label={t('Контакт для теста')} />
          <textarea className="input input-mono" rows={2} placeholder={t('контекст: { "cart_id": "c1", "cart_url": "https://…" }')} value={ctx} onChange={(e) => setCtx(e.target.value)} aria-label={t('Контекст')} />
          <button type="button" className="btn btn-sm" disabled={!contact.trim()} onClick={run}>{t('Проверить (ничего не отправляется)')}</button>
          {res && (
            <div className="stack" style={{ gap: 6 }}>
              <div className="small">{t('Решение:')} <b>{t(DECISION_LABEL[String(res.decision.action)] ?? String(res.decision.action))}</b>{res.decision.outcome ? <> · {t(OUTCOME_LABEL[String(res.decision.outcome)] ?? String(res.decision.outcome))}</> : null}{res.decision.reason ? <> · {String(res.decision.reason)}</> : null}</div>
              {res.email && (
                <>
                  <div className="small">{t('Тема:')} <b>{res.email.subject ?? '—'}</b></div>
                  {res.email.missing_variables.length > 0 && <div className="callout warn small">{t('Не хватает переменных: {list}', { list: res.email.missing_variables.join(', ') })}</div>}
                  {res.email.html && <iframe title={t('Письмо для этого контакта')} sandbox="" srcDoc={res.email.html} className="mail-preview" style={{ height: 260 }} />}
                </>
              )}
              {res.notification && (
                <div className="stack" style={{ gap: 4 }}>
                  {res.notification.error ? <div className="callout bad small"><div>{res.notification.error.message}</div></div> : res.notification.steps.map((x) => (
                    <div key={x.seq} className="row wrap small" style={{ gap: 6 }}>
                      <ChannelChip channel={x.channel} />
                      <StepPill state={x.state} />
                      {x.skip_reason && <span className="muted">{t(SKIP_REASON[x.skip_reason] ?? x.skip_reason)}</span>}
                      <span className="muted">{eur(x.estimated_cost)}</span>
                    </div>
                  ))}
                  {res.notification.estimated_cost_max && <div className="small">{t('Худший случай:')} <b>{eur(res.notification.estimated_cost_max.amount)}</b></div>}
                </div>
              )}
              {type !== 'send_email' && type !== 'send_notification' && <pre className="code" style={{ margin: 0 }}>{JSON.stringify(res.decision, null, 2)}</pre>}
            </div>
          )}
        </div>
      )}
    </details>
  );
}

// ---------- journey settings (the start node) ----------

function JourneySettings({ graph, patch, onClose, data, issues, disabled }: { graph: Graph; patch: Patch; onClose: () => void; data: PanelData; issues: ValidationIssue[]; disabled: boolean }) {
  const tg = graph.trigger;
  const setTrigger = (type: Trigger['type']) =>
    patch((g) => {
      g.trigger =
        type === 'event' ? { type, event: 'signup' } : type === 'segment_entry' ? { type, segment: data.segments[0]?.key ?? '' } : type === 'date' ? { type, attribute: 'birthday', offset_days: 0, at_local: '10:00', recurrence: 'yearly' } : { type: 'api' };
    });
  const d = graph.delivery;
  return (
    <div className="card step-panel">
      <div className="card-head">
        <h3>{t('Старт и правила сценария')}</h3>
        <button className="btn btn-ghost icon-btn" onClick={onClose} aria-label={t('Закрыть панель')}><IconX /></button>
      </div>
      <div className="card-pad stack">
        <Issues issues={issues.filter((i) => !i.path.startsWith('steps.'))} prefix="" />
        <Field label={t('Название')}><input className="input" value={graph.name ?? ''} onChange={(e) => patch((g) => { g.name = e.target.value; })} disabled={disabled} /></Field>
        <Field label={t('Триггер')}>
          <select className="select input" value={tg.type} onChange={(e) => setTrigger(e.target.value as Trigger['type'])} disabled={disabled}>
            {(Object.keys(TRIGGER_LABEL) as Trigger['type'][]).map((k) => <option key={k} value={k}>{t(TRIGGER_LABEL[k])}</option>)}
          </select>
        </Field>
        {tg.type === 'event' && (
          <>
            <Field label={t('Событие')}><input className="input input-mono" value={tg.event} onChange={(e) => patch((g) => { (g.trigger as { event: string }).event = e.target.value.trim(); })} disabled={disabled} /></Field>
            <Field label={t('Фильтр свойств (JSON)')} hint='[{ "property": "total", "op": "gt", "value": 0 }]'>
              <JsonInput value={tg.where} onChange={(v) => patch((g) => { const tr = g.trigger as { where?: PropFilter[] }; if (v === undefined) delete tr.where; else tr.where = v as PropFilter[]; })} disabled={disabled} label={t('Фильтр триггера')} rows={2} />
            </Field>
          </>
        )}
        {tg.type === 'segment_entry' && (
          <Field label={t('Сегмент')} hint={t('Вход контакта в сегмент запускает сценарий')}>
            <select className="select input" value={tg.segment} onChange={(e) => patch((g) => { (g.trigger as { segment: string }).segment = e.target.value; })} disabled={disabled}>
              {!data.segments.some((s) => s.key === tg.segment) && <option value={tg.segment}>{tg.segment || '—'}</option>}
              {data.segments.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
            </select>
          </Field>
        )}
        {tg.type === 'date' && (
          <div className="stack" style={{ gap: 8 }}>
            <Field label={t('Атрибут-дата')}><input className="input input-mono" list="journey-date-keys" value={tg.attribute} onChange={(e) => patch((g) => { (g.trigger as { attribute: string }).attribute = e.target.value.trim(); })} disabled={disabled} /></Field>
            <datalist id="journey-date-keys">{data.keys.filter((k) => k.type === 'date' || k.type === 'datetime').map((k) => <option key={k.key} value={k.key} />)}</datalist>
            <div className="inline-fields">
              <label className="small">{t('Сдвиг, дн.')}<input className="input" style={{ width: 80, marginLeft: 6 }} type="number" min={-365} max={365} value={tg.offset_days} onChange={(e) => patch((g) => { (g.trigger as { offset_days: number }).offset_days = Number(e.target.value); })} disabled={disabled} /></label>
              <label className="small">{t('в')}<input className="input" style={{ width: 110, marginLeft: 6 }} type="time" value={tg.at_local} onChange={(e) => patch((g) => { (g.trigger as { at_local: string }).at_local = e.target.value; })} disabled={disabled} /></label>
              <select className="select input" value={tg.recurrence} onChange={(e) => patch((g) => { (g.trigger as { recurrence: string }).recurrence = e.target.value; })} disabled={disabled} aria-label={t('Повтор')}>
                <option value="once">{t('один раз')}</option>
                <option value="yearly">{t('ежегодно')}</option>
              </select>
            </div>
          </div>
        )}
        {tg.type === 'api' && <div className="small muted">{t('Участие создаёт ваш сервер:')} <code>POST /v1/journeys/:key/enrollments</code> {t('с ключом идемпотентности.')}</div>}

        <Field label={t('Кто может войти')} hint={t('Пусто — любой контакт с триггером')}>
          <OptionalCondition value={graph.entry_filter} onChange={(c) => patch((g) => { if (c) g.entry_filter = c; else delete g.entry_filter; })} data={data} disabled={disabled} label={t('Фильтр входа')} />
        </Field>
        <Field label={t('Когда выйти досрочно')} hint={t('Проверяется перед каждым шагом')}>
          <OptionalCondition value={graph.exit_filter} onChange={(c) => patch((g) => { if (c) g.exit_filter = c; else delete g.exit_filter; })} data={data} disabled={disabled} label={t('Фильтр выхода')} />
        </Field>
        <Field label={t('Повторный вход')}>
          <div className="inline-fields">
            <select className="select input" value={graph.reentry?.mode ?? 'never'} onChange={(e) => patch((g) => { g.reentry = { mode: e.target.value as 'never', ...(e.target.value === 'after_cooldown' ? { cooldown_hours: g.reentry?.cooldown_hours ?? 24 } : {}) }; })} disabled={disabled}>
              <option value="never">{t('никогда')}</option>
              <option value="after_exit">{t('после завершения')}</option>
              <option value="after_cooldown">{t('после паузы')}</option>
            </select>
            {graph.reentry?.mode === 'after_cooldown' && <><input className="input" style={{ width: 90 }} type="number" min={1} value={graph.reentry.cooldown_hours ?? 24} onChange={(e) => patch((g) => { g.reentry!.cooldown_hours = Number(e.target.value); })} disabled={disabled} aria-label={t('Пауза, ч')} /><span className="small muted">{t('ч')}</span></>}
          </div>
        </Field>

        <div className="stack" style={{ gap: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
          <b className="small">{t('Цель и контрольная группа')}</b>
          <label className="row small" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={!!graph.goal} onChange={(e) => patch((g) => { if (e.target.checked) g.goal = { event: 'order.paid', window_days: 7, exit_on_goal: true }; else { delete g.goal; g.holdout = { percent: 0 }; } })} disabled={disabled} />
            {t('Есть цель')}
          </label>
          {graph.goal && (
            <>
              <div className="inline-fields">
                <input className="input input-mono" style={{ flex: 1, minWidth: 120 }} value={graph.goal.event} onChange={(e) => patch((g) => { g.goal!.event = e.target.value.trim(); })} disabled={disabled} aria-label={t('Событие цели')} />
                <span className="small muted">{t('за')}</span>
                <input className="input" style={{ width: 70 }} type="number" min={1} max={90} value={graph.goal.window_days} onChange={(e) => patch((g) => { g.goal!.window_days = Number(e.target.value); })} disabled={disabled} aria-label={t('Окно цели, дней')} />
                <span className="small muted">{t('дн.')}</span>
              </div>
              <label className="row small" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={graph.goal.exit_on_goal} onChange={(e) => patch((g) => { g.goal!.exit_on_goal = e.target.checked; })} disabled={disabled} /> {t('Выходить при достижении цели')}
              </label>
              <Field label={t('Свойство ценности')} hint={t('Например total — сумма заказа для отчёта')}><input className="input input-mono" value={graph.goal.value_property ?? ''} onChange={(e) => patch((g) => { if (e.target.value.trim()) g.goal!.value_property = e.target.value.trim(); else delete g.goal!.value_property; })} disabled={disabled} /></Field>
              <Field label={t('Контрольная группа (holdout), %')} hint={t('Писем не получает: только так измеряется прирост. 0 — прирост не измеряется')}>
                <input className="input" type="number" min={0} max={50} value={graph.holdout?.percent ?? 0} onChange={(e) => patch((g) => { g.holdout = { percent: Number(e.target.value) }; })} disabled={disabled} />
              </Field>
            </>
          )}
        </div>

        <div className="stack" style={{ gap: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
          <b className="small">{t('Доставка')}</b>
          <Field label={t('Поток по умолчанию')}>
            <select className="select input" value={d.stream} onChange={(e) => patch((g) => { g.delivery.stream = e.target.value as Delivery['stream']; })} disabled={disabled}>
              <option value="marketing">{t('маркетинг')}</option>
              <option value="transactional">{t('транзакционные (сервисные)')}</option>
            </select>
          </Field>
          <div className="inline-fields">
            <input className="input" style={{ flex: 2, minWidth: 160 }} type="email" placeholder={t('hello@ваш-домен')} value={d.from?.email ?? ''} onChange={(e) => patch((g) => { g.delivery.from = { ...(g.delivery.from ?? {}), email: e.target.value.trim() }; })} disabled={disabled} aria-label={t('Адрес отправителя')} />
            <input className="input" style={{ flex: 1, minWidth: 110 }} placeholder={t('Имя')} value={d.from?.name ?? ''} onChange={(e) => patch((g) => { if (g.delivery.from) { if (e.target.value) g.delivery.from.name = e.target.value; else delete g.delivery.from.name; } })} disabled={disabled} aria-label={t('Имя отправителя')} />
          </div>
          {d.stream === 'marketing' && (
            <>
              <label className="row small" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={!!d.quiet_hours} onChange={(e) => patch((g) => { if (e.target.checked) g.delivery.quiet_hours = { start: '21:00', end: '09:00' }; else delete g.delivery.quiet_hours; })} disabled={disabled} />
                {t('Тихие часы по времени получателя')}
              </label>
              {d.quiet_hours && (
                <div className="inline-fields">
                  <input className="input" type="time" value={d.quiet_hours.start} onChange={(e) => patch((g) => { g.delivery.quiet_hours!.start = e.target.value; })} disabled={disabled} aria-label={t('Начало тихих часов')} />
                  <span className="small muted">—</span>
                  <input className="input" type="time" value={d.quiet_hours.end} onChange={(e) => patch((g) => { g.delivery.quiet_hours!.end = e.target.value; })} disabled={disabled} aria-label={t('Конец тихих часов')} />
                </div>
              )}
              <div className="inline-fields">
                <span className="small">{t('Умная отправка, ч')}</span>
                <input className="input" style={{ width: 80 }} type="number" min={0} max={168} value={d.smart_sending_hours ?? 16} onChange={(e) => patch((g) => { g.delivery.smart_sending_hours = Number(e.target.value); })} disabled={disabled} aria-label={t('Умная отправка, ч')} />
              </div>
            </>
          )}
          <div className="inline-fields">
            <span className="small">{t('Не позже чем через, ч')}</span>
            <input className="input" style={{ width: 80 }} type="number" min={1} max={720} value={d.max_lateness_hours ?? ''} placeholder="∞" onChange={(e) => patch((g) => { g.delivery.max_lateness_hours = e.target.value ? Number(e.target.value) : null; })} disabled={disabled} aria-label={t('Допустимое опоздание, ч')} />
          </div>
          <div className="small muted">{t('Опоздавшее письмо (например, после долгой паузы) пропускается, а не уходит невпопад.')}</div>
        </div>
      </div>
    </div>
  );
}

function OptionalCondition({ value, onChange, data, disabled, label }: { value: Condition | undefined; onChange: (c: Condition | undefined) => void; data: PanelData; disabled: boolean; label: string }) {
  if (!value) {
    return disabled ? <span className="small muted">{t('не задано')}</span> : <button type="button" className="btn btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => onChange({ consent: 'marketing', status: 'granted' })}><IconPlus size={14} /> {t('Добавить условие')}</button>;
  }
  return (
    <div className="stack" style={{ gap: 6 }}>
      <ConditionInput value={value} onChange={onChange} keys={data.keys} segments={data.segments} disabled={disabled} label={label} />
      {!disabled && <button type="button" className="btn btn-sm btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={() => onChange(undefined)}>{t('Убрать условие')}</button>}
    </div>
  );
}

/** What a dry run of a step decided, in words (the engine's tokens stay in the API). */
const DECISION_LABEL: Record<string, string> = {
  send: 'отправить письмо',
  notify: 'отправить уведомление по каналам',
  wait: 'ждать',
  goto: 'перейти к шагу',
  exit: 'выйти из сценария',
  skip: 'пропустить шаг',
  hold: 'придержать',
  update: 'обновить контакт',
  webhook: 'вызвать вебхук',
  digest_close: 'закрыть дайджест',
  fail: 'ошибка шага',
};
const OUTCOME_LABEL: Record<string, string> = {
  event_received: 'событие получено',
  failed_render: 'шаблон не собрался',
  skipped_no_channel: 'пропуск: нет доступного канала',
  skipped_stale: 'пропуск: опоздание больше 24 ч',
  timeout: 'время ожидания вышло',
  waited: 'ожидание завершено',
};
